import { useIsMobile } from '../../hooks/useIsMobile';
import { ACCENT, BORDER, FONT_BODY, FONT_MONO, MUTED, SURFACE2, TEXT_SECONDARY } from '../../tokens';
import type { View } from '../../types';

const CONTACT_EMAIL = 'Brody.Zwiers@augment-os.net';
const ABN = 'ABN 77 318 303 608';

interface FooterLink {
  label: string;
  href: string;
}

const FOOTER_LINKS: readonly FooterLink[] = [
  { label: 'Problem', href: '#problem' },
  { label: 'Solution', href: '#solution' },
  { label: 'Included', href: '#included' },
];

interface SocialLink {
  label: string;
  href: string;
  icon: React.ReactNode;
}

const SOCIALS: readonly SocialLink[] = [
  {
    label: 'YouTube',
    href: 'https://www.youtube.com/channel/UCWnkXvaHFNL7H-YQR-vQ2Bg',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M23.498 6.186a2.996 2.996 0 0 0-2.107-2.118C19.51 3.5 12 3.5 12 3.5s-7.51 0-9.391.568a2.996 2.996 0 0 0-2.107 2.118C0 8.066 0 12 0 12s0 3.934.502 5.814a2.996 2.996 0 0 0 2.107 2.118C4.49 20.5 12 20.5 12 20.5s7.51 0 9.391-.568a2.996 2.996 0 0 0 2.107-2.118C24 15.934 24 12 24 12s0-3.934-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
      </svg>
    ),
  },
  {
    label: 'Instagram',
    href: 'https://www.instagram.com/brody.zwierz/',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
        <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
        <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
      </svg>
    ),
  },
  {
    label: 'LinkedIn',
    href: 'https://www.linkedin.com/in/brody-zwiers-5206b1365/',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
      </svg>
    ),
  },
];

interface FooterProps {
  onAbout: () => void;
  onHome: () => void;
  currentView: View;
}

export function Footer({ onAbout, onHome, currentView }: FooterProps) {
  const isMobile = useIsMobile();

  // From the About view an in-page anchor has no target, so return Home first
  // and scroll once the section has rendered.
  const handleFooterClick = (event: React.MouseEvent, href: string): void => {
    if (currentView !== 'about') return;

    event.preventDefault();
    onHome();

    requestAnimationFrame(() => {
      setTimeout(() => {
        document.getElementById(href.replace('#', ''))?.scrollIntoView({ behavior: 'smooth' });
      }, 80);
    });
  };

  const linkHover = (e: React.MouseEvent<HTMLElement>, color: string): void => {
    e.currentTarget.style.color = color;
  };

  return (
    <footer
      style={{
        background: SURFACE2,
        borderTop: `1px solid ${BORDER}`,
        padding: isMobile ? '40px 20px' : '48px 64px',
      }}
    >
      <div
        style={{
          maxWidth: 1200,
          margin: '0 auto',
          display: 'flex',
          justifyContent: isMobile ? 'flex-start' : 'space-between',
          alignItems: isMobile ? 'stretch' : 'center',
          flexDirection: isMobile ? 'column' : 'row',
          gap: 24,
        }}
      >
        <div style={{ alignSelf: isMobile ? 'flex-start' : undefined }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontWeight: 900,
              fontSize: '1rem',
              letterSpacing: '-0.03em',
              marginBottom: 6,
            }}
          >
            <img
              src="/logos/augment-os-logo.png"
              alt="Augment OS"
              style={{ width: 26, height: 26, borderRadius: 6, display: 'block' }}
            />
            Augment OS
          </div>
          <div style={{ fontFamily: FONT_MONO, fontSize: '0.65rem', color: MUTED, letterSpacing: '0.06em' }}>
            Content, leads and reviews. Run as one system.
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 10,
            alignSelf: isMobile ? 'center' : undefined,
          }}
        >
          <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
            <button
              type="button"
              onClick={onAbout}
              style={{
                background: 'transparent',
                border: 'none',
                color: TEXT_SECONDARY,
                fontSize: '0.8rem',
                cursor: 'pointer',
                fontFamily: FONT_BODY,
                textDecoration: 'none',
                transition: 'color 0.2s',
                padding: 0,
              }}
              onMouseEnter={(e) => linkHover(e, ACCENT)}
              onMouseLeave={(e) => linkHover(e, TEXT_SECONDARY)}
            >
              About
            </button>

            {FOOTER_LINKS.map((link) => (
              <span key={link.href} style={{ display: 'contents' }}>
                <span style={{ color: 'rgba(255,255,255,0.1)' }}>|</span>
                <a
                  href={link.href}
                  onClick={(e) => handleFooterClick(e, link.href)}
                  style={{
                    color: TEXT_SECONDARY,
                    fontSize: '0.8rem',
                    textDecoration: 'none',
                    transition: 'color 0.2s',
                  }}
                  onMouseEnter={(e) => linkHover(e, ACCENT)}
                  onMouseLeave={(e) => linkHover(e, TEXT_SECONDARY)}
                >
                  {link.label}
                </a>
              </span>
            ))}
          </div>

          <div
            style={{
              fontFamily: FONT_MONO,
              fontSize: '0.62rem',
              color: MUTED,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              marginTop: 4,
            }}
          >
            Follow
          </div>

          <div style={{ display: 'flex', gap: 14 }}>
            {SOCIALS.map((social) => (
              <a
                key={social.label}
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={social.label}
                style={{ color: '#666', transition: 'color 0.2s' }}
                onMouseEnter={(e) => linkHover(e, ACCENT)}
                onMouseLeave={(e) => linkHover(e, '#666')}
              >
                {social.icon}
              </a>
            ))}
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: 6,
            alignSelf: isMobile ? 'flex-end' : undefined,
          }}
        >
          <a
            href={`mailto:${CONTACT_EMAIL}`}
            style={{ color: TEXT_SECONDARY, fontSize: '1rem', textDecoration: 'none', transition: 'color 0.2s' }}
            onMouseEnter={(e) => linkHover(e, ACCENT)}
            onMouseLeave={(e) => linkHover(e, TEXT_SECONDARY)}
          >
            {CONTACT_EMAIL}
          </a>
          <div style={{ fontFamily: FONT_MONO, fontSize: '0.6rem', color: MUTED, letterSpacing: '0.06em' }}>
            {ABN}
          </div>
        </div>
      </div>
    </footer>
  );
}
